import React, { useEffect, useMemo, useState } from 'react';
import { DragSmall, Key, Plus, Refresh, Search, Settings, Sorting, X } from '@fabric-ui/icons';
import { WarningFullOrange } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { arrayEquals, iconSize, rearrange } from '@fabric-ui/utils';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Button, ClickAwayListener, Divider, Grow, IconButton, Paper, Popper, Typography } from '@mui/material';
import { FieldType } from './context.type';
import { AvButton, AvDivider, AvTooltip, TextInput } from './index';
import { TColumnsDef } from './TableV2';
import { typeFormatter } from './utils';

const clearIcon = <X style={iconSize(18)} />;
const plusIcon = <Plus style={iconSize(18)} />;
const sortingIcon = <Sorting style={iconSize(20)} />;
const settingsIcon = size => <Settings style={iconSize(size)} />;
const dragIcon = <DragSmall />;
const refreshIcon = <Refresh style={{ ...iconSize(18), transform: 'scaleX(-1)' }} />;
const closeButton = <X style={iconSize(24)} />;
const keyIcon = color => <Key style={{ ...iconSize(12), color }} />;

export type ColumnReorderItem = { id: string; label: React.ReactNode; hidden: boolean; type?: FieldType; [key: string]: any };
type TNewItem = TColumnsDef[0];
type TItem = ColumnReorderItem | TNewItem;

const isOldItem = (item: TItem): item is ColumnReorderItem => {
  try {
    return 'label' in item;
  } catch (e) {
    console.error(`Item in isOldItem function passed undefined - added for debug`, { e, item });
    throw e;
  }
};

export interface ColumnReorderProps<T extends TItem> {
  items: T[];
  setItems: (items: T[]) => void;
  initialColumns: T[];
  firstIndexKey?: boolean;
  disabled?: boolean;
  sortOnly?: boolean;
  width?: number;
  // Ranks an item into a group. Items are stably sorted by ascending rank and a horizontal divider
  // is rendered wherever the rank changes
  splitGroupsBy?: (item: T) => number;
}

export const ColumnReorder = <T extends TItem>({
  items: originalItems,
  setItems: setOriginalItems,
  initialColumns,
  firstIndexKey = false,
  disabled = false,
  sortOnly = false,
  width = 20,
  splitGroupsBy,
}: ColumnReorderProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Prefer the precise `fieldType`; `type` is often a cell formatter type (e.g. 'bool') with no icon
  const getType = (item: TItem): FieldType | undefined => (isOldItem(item) ? (item.fieldType ?? item.type) : undefined);
  const partitionByGroup = (arr: T[]): T[] => (splitGroupsBy ? arr.toSorted((a, b) => splitGroupsBy(a) - splitGroupsBy(b)) : arr);

  const [items, _setItems] = useState<T[]>(partitionByGroup(originalItems));
  // All state updates go through this wrapper so items are always kept in group order
  // when `splitGroupsBy` is given — no call site needs to remember to partition.
  const setItems = (next: T[]) => _setItems(partitionByGroup(next));
  const [search, setSearch] = useState('');

  useEffect(() => setItems(originalItems), [originalItems]);

  const handleClick = ({ currentTarget }: React.MouseEvent<HTMLElement>) => setAnchorEl(anchorEl ? null : currentTarget);

  const onClose = () => {
    setAnchorEl(null);
    setItems(originalItems);
    setSearch('');
  };

  const apply = () => {
    setOriginalItems(items);
    onClose();
  };

  const getLabel = (item: TItem): string => {
    if (isOldItem(item)) {
      return (item.label || item.id || '') as string;
    }

    const { header, id } = item;
    if (typeof header === 'string') {
      return header;
    }
    return id || '';
  };

  const isHidden = (item: TItem) => {
    if (isOldItem(item)) {
      return item.hidden;
    }
    return !!item.meta?.hidden;
  };

  const filteredItems = items.filter(v => getLabel(v).toLowerCase().includes(search.toLowerCase()));

  const hasChanges = useMemo(() => {
    const comparator = (a: TItem, b: TItem) => a.id === b.id && isHidden(a) === isHidden(b);
    return !arrayEquals(items, initialColumns, comparator);
  }, [items, initialColumns]);

  const MAX_RENDER_COUNT = 200;
  const allAvailable = filteredItems.filter(item => isHidden(item));
  const assortedItems = {
    available: allAvailable.slice(0, MAX_RENDER_COUNT),
    used: filteredItems.filter(item => !isHidden(item)),
  };

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return;
    }

    const sourceList = source.droppableId === 'used' ? assortedItems.used : assortedItems.available;
    const itemToMove = sourceList[source.index];
    if (!itemToMove) return;

    const sourceIndex = items.findIndex(i => i.id === itemToMove.id);

    const destList = destination.droppableId === 'used' ? assortedItems.used : assortedItems.available;
    const itemToReplace = destList[destination.index];
    const destinationIndex = itemToReplace ? items.findIndex(i => i.id === itemToReplace.id) : items.length;

    if (sourceIndex > -1 && destinationIndex > -1 && sourceIndex !== destinationIndex) {
      const isTableV2 = !isOldItem(items[sourceIndex]);
      const newItems = rearrange(
        items,
        items[sourceIndex],
        destinationIndex,
        'id',
        source.droppableId !== destination.droppableId,
        isTableV2
      ) as T[];
      setItems(newItems);
    }
  };

  const toggleVisible = (id: string, hidden: boolean) => {
    const newItems = items.map(item => {
      if (item.id === id) {
        if (isOldItem(item)) {
          return { ...item, hidden };
        }
        return { ...item, meta: { ...(item.meta || {}), hidden } };
      }
      return item;
    });
    setItems(newItems);
  };

  const isGroupBoundary = (list: T[], index: number) => {
    const next = list[index + 1];
    return !!splitGroupsBy && !!next && splitGroupsBy(list[index]) !== splitGroupsBy(next);
  };

  const buttonClass = [anchorEl && 'Mui-focusVisible', 'transparent'].filter(Boolean).join(' ');

  const itemFunc = (itemList: typeof items, icon?: React.JSX.Element) =>
    function dragItem(providedDrag, snapshotDrag, rubric) {
      const item = itemList[rubric.source.index];
      const label = getLabel(item);
      const tooltipContent = label || item.id;
      const typeIcon = typeFormatter(getType(item));

      return (
        <Box
          ref={providedDrag.innerRef}
          {...providedDrag.draggableProps}
          {...providedDrag.dragHandleProps}
          sx={getItemStyle(snapshotDrag.isDragging, providedDrag.draggableProps.style)}>
          <Box sx={{ ...flex.row, ...flex.itemsCenter, gap: 0.5, overflow: 'hidden' }}>
            {dragIcon}
            {React.isValidElement(typeIcon) && typeIcon}
            <AvTooltip>{tooltipContent}</AvTooltip>
            {isOldItem(item) && item.warningTooltip && (
              <AvTooltip title={item.warningTooltip} hasWidthLimit={false}>
                <span className="flex shrink-0 items-center justify-center">
                  <WarningFullOrange style={iconSize(16)} />
                </span>
              </AvTooltip>
            )}
          </Box>
          {icon}
        </Box>
      );
    };

  return (
    <>
      <Button
        variant="filter"
        disabled={disabled}
        className={buttonClass}
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={handleClick}
        sx={{ px: 1 }}
        title="Manage Table Columns">
        {settingsIcon(width)}
      </Button>
      {anchorEl && (
        <Popper
          open={Boolean(anchorEl)}
          placement="bottom-start"
          anchorEl={anchorEl}
          transition
          sx={{ zIndex: ({ zIndex }) => zIndex.modal, borderRadius: '2px', position: 'relative' }}>
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom-end' ? 'right top' : 'right bottom' }}>
              <Paper sx={{ ...flex.col }}>
                <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                  <div>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16 }}>
                      {closeButton}
                    </IconButton>
                    <Box sx={{ ...(sortOnly ? {} : { ...flex.itemsCenter, pb: 1 }), pl: 3 }}>
                      <Box
                        sx={{
                          color: 'var(--color-content-base-primary)',
                          fontSize: 16,
                          fontWeight: 600,
                          pt: 3,
                          pr: 1,
                          pb: 2,
                        }}>
                        Manage Table Columns
                      </Box>
                      <TextInput
                        sx={{ width: 220, pb: sortOnly ? 1 : undefined, pl: sortOnly ? 1 : undefined, pt: !sortOnly ? 1 : undefined }}
                        enableAnimation={!sortOnly}
                        helperText="Search"
                        value={search}
                        onChange={setSearch}
                        icon={<Search style={{ ...iconSize(18) }} />}
                        size="small"
                      />
                    </Box>

                    <DragDropContext onDragEnd={onDragEnd}>
                      <Box sx={style}>
                        {!sortOnly && (
                          <Droppable droppableId="available" renderClone={itemFunc(assortedItems.available)}>
                            {(provided, snapshot) => (
                              <Box
                                component="section"
                                className="available"
                                data-dragorigin={snapshot.draggingFromThisWith && !snapshot.isDraggingOver}>
                                <Typography variant="h7" sx={{ color: 'var(--color-content-base-tertiary)' }}>
                                  Available ({allAvailable.length})
                                  {allAvailable.length > MAX_RENDER_COUNT && !search && (
                                    <Box component="span" sx={{ fontSize: 11, fontWeight: 400 }}>
                                      {' '}
                                      — search to find more
                                    </Box>
                                  )}
                                </Typography>
                                <Box {...provided.droppableProps} ref={provided.innerRef} sx={listStyle}>
                                  {assortedItems.available.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                      <Box sx={itemWrapperStyle(false, isGroupBoundary(assortedItems.available, index))}>
                                        <Draggable draggableId={item.id} index={index} key={item.id}>
                                          {itemFunc(
                                            assortedItems.available,
                                            <AvButton
                                              variant="link"
                                              size="xSmall"
                                              title="Add"
                                              onClick={() => toggleVisible(item.id!, false)}>
                                              {plusIcon}
                                            </AvButton>
                                          )}
                                        </Draggable>
                                      </Box>
                                      {isGroupBoundary(assortedItems.available, index) && <AvDivider />}
                                    </React.Fragment>
                                  ))}
                                  {provided.placeholder}
                                </Box>
                              </Box>
                            )}
                          </Droppable>
                        )}
                        {!sortOnly && <Box sx={{ ...flex.itemsCenter, color: 'var(--color-content-base-subdued)' }}>{sortingIcon}</Box>}
                        <Droppable droppableId="used" renderClone={itemFunc(assortedItems.used)}>
                          {(provided, snapshot) => (
                            <Box
                              component="section"
                              className="used"
                              data-dragorigin={snapshot.draggingFromThisWith && !snapshot.isDraggingOver}>
                              <Typography variant="h7" sx={{ color: 'var(--color-content-interactive-primary-default)' }}>
                                In Use ({assortedItems.used.length})
                              </Typography>
                              <Box {...provided.droppableProps} ref={provided.innerRef} sx={listStyle}>
                                {assortedItems.used.map((item, index) => (
                                  <React.Fragment key={item.id}>
                                    <Box sx={itemWrapperStyle(true, isGroupBoundary(assortedItems.used, index))}>
                                      <Draggable
                                        draggableId={item.id!}
                                        index={index}
                                        key={item.id}
                                        isDragDisabled={assortedItems.used.length === 1}>
                                        {itemFunc(
                                          assortedItems.used,
                                          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
                                            {firstIndexKey && items.findIndex(i => i.id === item.id) === 0
                                              ? keyIcon('var(--color-content-interactive-primary-default)')
                                              : null}
                                            {assortedItems.used.length > 1 && !sortOnly ? (
                                              <AvButton
                                                variant="link"
                                                size="xSmall"
                                                title="Clear"
                                                onClick={() => toggleVisible(item.id!, true)}>
                                                {clearIcon}
                                              </AvButton>
                                            ) : null}
                                          </Box>
                                        )}
                                      </Draggable>
                                    </Box>
                                    {isGroupBoundary(assortedItems.used, index) && <AvDivider variant="info" />}
                                  </React.Fragment>
                                ))}
                                {provided.placeholder}
                              </Box>
                            </Box>
                          )}
                        </Droppable>
                      </Box>
                    </DragDropContext>
                    <Divider />
                    <Box sx={{ ...flex.justifyBetween, gap: 3, padding: '12px 24px' }}>
                      {hasChanges ? (
                        <Button onClick={() => setItems(initialColumns)} variant="outlined" size="small">
                          {refreshIcon} {sortOnly ? '' : 'Reset to '}Default
                        </Button>
                      ) : (
                        <div />
                      )}
                      <Box sx={{ ...flex.row, gap: 3 }}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={apply} variant="contained" size="small">
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
};

const style = () => ({
  ...flex.row,
  gap: 1,
  px: 4,
  pb: 4,
  section: {
    ...flex.col,
    gap: 2,
    borderRadius: '2px',
    p: 3,
    minWidth: 300,
    '&.available': {
      backgroundColor: 'var(--color-surface-base-secondary)',
      border: '1px solid var(--color-border-base-primary)',
    },
    '&.used': {
      backgroundColor: 'var(--color-surface-interactive-modifier-default)',
      border: '1px solid var(--color-border-base-primary)',
    },
    '&[data-dragorigin="true"] ~ section, &:has(~ [data-dragorigin="true"])': {
      outline: '2px dashed var(--color-border-interactive-primary-default)',
    },
  },
});

const itemWidth = 250;
const emptyOutlineWidth = 2;
const itemWrapperStyle = (hasNumbers?, atGroupBoundary?) => ({
  position: 'relative',
  ...(atGroupBoundary && { '& > div': { mb: 0 } }),
  '&:empty': {
    outline: '2px solid var(--color-border-interactive-secondary-default)',
    top: -4,
    left: (hasNumbers ? 24 : 0) + emptyOutlineWidth,
    width: itemWidth - emptyOutlineWidth * 2,
  },
  ...(hasNumbers && {
    '+ [data-rbd-placeholder-context-id]': {
      position: 'relative',
    },
    '&:not(:empty), + [data-rbd-placeholder-context-id]': {
      counterIncrement: 'column',
      pl: 3,
      '&:before': {
        content: 'counter(column)',
        position: 'absolute',
        top: 4,
        left: 0,
        fontWeight: 600,
        fontSize: 12,
        color: 'var(--color-content-interactive-primary-default)',
      },
    },
  }),
});

const getItemStyle = (isDragging, draggableStyle) => () => ({
  ...flex.justifyBetween,
  flexGrow: 1,
  width: itemWidth,
  height: 24,
  mb: 1,
  userSelect: 'none',
  padding: '2px 12px',
  borderRadius: '10px',
  border: '1px solid',
  borderColor: isDragging ? 'var(--color-border-interactive-primary-default)' : 'var(--color-border-interactive-secondary-default)',
  backgroundColor: 'var(--color-surface-base-primary)',
  fontSize: 13,
  fontWeight: 600,
  ...draggableStyle,
  '&:hover': {
    borderColor: isDragging ? 'var(--color-border-interactive-primary-default)' : 'var(--color-border-interactive-secondary-hover)',
  },
  svg: {
    color: isDragging ? 'var(--color-content-interactive-primary-default)' : undefined,
  },
});

const listStyle = {
  ...flex.col,
  height: '100%',
  maxHeight: 200,
  overflow: 'auto',
};
